import React from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { generatePath } from 'react-router-dom';
import { Col, Row } from 'react-styled-flexboxgrid';
import { useMedia, useTitle } from 'react-use';

import {
  BUTTON_TYPES,
  FEATURE_FLAGS_FOR_SELLER,
  media,
} from '@savgroup-front-common/constants';
import { useGetFeatureFlagsQuery } from '@savgroup-front-common/core/api/Authorization/useGetFeatureFlagsQuery';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { Input } from '@savgroup-front-common/core/atoms/Form';
import { Heading } from '@savgroup-front-common/core/atoms/Heading';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import {
  buildPageTitle,
  SUPPORTED_ICON_POSITIONS,
} from '@savgroup-front-common/core/helpers';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { PhoneNumberInputHookForm } from '@savgroup-front-common/core/molecules/PhoneNumberInputHookForm/PhoneNumberInputHookForm';
import { useRoutedStepsOrchestratorContext } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.context';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import ArrowNakedLeftIcon from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedLeft.icon';
import { ArrowNakedRightIcon } from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedRight.icon';
import { MyAccountIcon } from '@savgroup-front-common/core/protons/IconsNewDesign/MyAccount.icon';
import { HandlingSummary } from '@savgroup-front-common/types';
import { ICONS_TYPE } from '@savgroup-front-common/types/Icon';

import { ResumeCard } from '@/view/components/ResumeCard';
import { ResumeCardButton } from '@/view/components/ResumeCardButton';

import { FINAL_ROUTES } from '../../../app';
import { STEP_CONFIG } from '../../../app/NewLayout/ClassiqueRoutes.config';
import { useLayout } from '../../../app/NewLayout/NewLayout.hooks';
import {
  NewStepLayout,
  PageFooterContainer,
} from '../../../app/NewLayout/NewStepLayout';
import { ResumeDelivery } from '../../../components/ResumeDelivery';
import { ResumeDeposit } from '../../../components/ResumeDeposit';
import { ResumeSolution } from '../../../components/ResumeSolution';
import { IrshStepValues } from '../IrshPages.types';

import AgreementCheckbox from './AgreementCheckbox';
import messages from './messages';
import useClaimGroupConfirmationPage from './NewClaimGroupConfirmationPage.hooks';
import {
  $AgreementRow,
  $ConfirmationItemWrapper,
  $ConfirmationResultWrapper,
  $ConfirmationWrapper,
  $Grid,
  $HeadingIcon,
  $LeftColumn,
} from './NewClaimGroupConfirmationPage.styles';
import PaymentCard from './PaymentCard/PaymentCard';

export const NewClaimGroupConfirmationPage: React.FC<
  RoutedStepProps<IrshStepValues>
> = ({ onNextStep, onPreviousStep }) => {
  const { values } = useRoutedStepsOrchestratorContext<IrshStepValues>();
  const handling = values?.handling as HandlingSummary;
  const { login } = useLayout();
  const hasDeposit = handling?.hasDeposit;
  const hasDelivery = handling?.hasDelivery;

  const isMobileView = useMedia(media.maxWidth.xs);
  const {
    claimGroupConfirmation,
    claimGroupId,
    formContext,
    onSubmit,
    agreementUrl,
    isLoading,
    paymentRequired,
    sellerId,
    isLoadingSubmit,
    isCheckInvoiceLoading,
    isMonextPayment,
  } = useClaimGroupConfirmationPage({
    onNextStep,
  });

  const {
    control,
    formState: { isValid, errors },
    watch,
    setValue,
  } = formContext;

  const cgv = watch('cgv');
  const shouldDisplayPaymentCard =
    cgv && paymentRequired && sellerId && !isCheckInvoiceLoading;

  const isTransportPlanFeatureEnabled = useGetFeatureFlagsQuery({
    featureFlag: [FEATURE_FLAGS_FOR_SELLER.TRANSPORT_PLAN],
    sellerId,
  });

  useTitle(buildPageTitle([messages.claimGroupConfirmationTitlePage]));

  if (isLoading || !claimGroupId) {
    return <BaseLoader />;
  }

  return (
    <NewStepLayout
      stepName={STEP_CONFIG.CLAIM_GROUP_CONFIRMATION}
      withoutMarginBottom
    >
      <$Grid fluid>
        <FormProvider {...formContext}>
          <$ConfirmationWrapper>
            <div>
              <form onSubmit={onSubmit}>
                <$LeftColumn>
                  <ResumeSolution
                    claimGroupId={claimGroupId}
                    claimGroupConfirmation={claimGroupConfirmation}
                    customBuildUpdateSolutionUrl={generatePath(
                      FINAL_ROUTES.CLAIM_GROUP_SOLUTION,
                      {
                        claimGroupId,
                      },
                    )}
                    isModifyDisabled={isMonextPayment}
                  />
                  {hasDeposit && (
                    <ResumeDeposit
                      claimGroupConfirmation={claimGroupConfirmation}
                      handlingSummary={
                        isTransportPlanFeatureEnabled ? handling : undefined
                      }
                    />
                  )}
                  {hasDelivery && (
                    <ResumeDelivery
                      claimGroupConfirmation={claimGroupConfirmation}
                      handlingSummary={
                        isTransportPlanFeatureEnabled ? handling : undefined
                      }
                    />
                  )}
                  <$ConfirmationItemWrapper>
                    <Heading level={3}>
                      <$HeadingIcon>
                        <MyAccountIcon icon={ICONS_TYPE.SEND_MAIL_ICON} />
                      </$HeadingIcon>
                      <SafeFormattedMessageWithoutSpread
                        message={messages.notificationsTitle}
                      />
                    </Heading>
                    <Row>
                      <Col sm={6} xs={12}>
                        <Controller
                          control={control}
                          name="email"
                          render={({ field }) => {
                            return (
                              <Input
                                name={field.name}
                                value={field.value}
                                label={messages.mail}
                                disableBrowserAutocomplete
                                errors={errors}
                                onChange={field.onChange}
                              />
                            );
                          }}
                        />
                      </Col>
                      <Col sm={6} xs={12}>
                        <PhoneNumberInputHookForm
                          name="phone"
                          control={control}
                          label={messages.phone}
                          disableBrowserAutocomplete
                          errors={errors}
                          onChange={(_, meta) => {
                            if (meta) {
                              setValue('phoneCountryCode', meta.country.iso2);
                            }
                          }}
                          defaultCountry={login.countryCode}
                        />
                      </Col>
                    </Row>
                    <$AgreementRow>
                      <Col sm={12} xs={12}>
                        <Controller
                          control={control}
                          name="cgv"
                          render={({ field }) => {
                            return (
                              <AgreementCheckbox
                                agreementHref={agreementUrl}
                                isChecked={field.value}
                                onChange={field.onChange}
                              />
                            );
                          }}
                        />
                      </Col>
                    </$AgreementRow>
                  </$ConfirmationItemWrapper>
                </$LeftColumn>
                <PageFooterContainer reverseChildren={!paymentRequired}>
                  {!paymentRequired && (
                    <Button
                      primary
                      type={BUTTON_TYPES.BUTTON}
                      icon={<ArrowNakedRightIcon />}
                      position={SUPPORTED_ICON_POSITIONS.RIGHT}
                      disabled={!isValid}
                      isLoading={isLoadingSubmit}
                      onClick={onSubmit}
                      dataTestId="nextButton"
                      ariaLabel={messages.confirmButton}
                    >
                      <SafeFormattedMessageWithoutSpread
                        message={messages.confirmButton}
                      />
                    </Button>
                  )}

                  <Button
                    type={BUTTON_TYPES.BUTTON}
                    tertiary
                    onClick={() => {
                      onPreviousStep({});
                    }}
                    disabled={isMonextPayment}
                    icon={<ArrowNakedLeftIcon />}
                    position={SUPPORTED_ICON_POSITIONS.LEFT}
                    dataTestId="previousButton"
                    ariaLabel={messages.previousButton}
                  >
                    <SafeFormattedMessageWithoutSpread
                      message={messages.previousButton}
                    />
                  </Button>
                </PageFooterContainer>
              </form>
              {shouldDisplayPaymentCard && (
                <PaymentCard
                  sellerId={sellerId}
                  onSubmitSuccess={onSubmit}
                  isFormCompleted={isValid}
                />
              )}
            </div>
            <$ConfirmationResultWrapper>
              {isMobileView && (
                <ResumeCardButton
                  claimGroupConfirmation={claimGroupConfirmation}
                  hasPrice
                  hasDepositPrice
                  hasDeliveryPrice
                  isTransportPlanFeatureEnabled={isTransportPlanFeatureEnabled}
                  isModifyDisabled={isMonextPayment}
                />
              )}
              {!isMobileView && (
                <ResumeCard
                  claimGroupConfirmation={claimGroupConfirmation}
                  hasPrice
                  hasDepositPrice
                  hasDeliveryPrice
                  isTransportPlanFeatureEnabled={isTransportPlanFeatureEnabled}
                  isModifyDisabled={isMonextPayment}
                />
              )}
            </$ConfirmationResultWrapper>
          </$ConfirmationWrapper>
        </FormProvider>
      </$Grid>
    </NewStepLayout>
  );
};
